/** The four edges `placePopover` reads; a `DOMRect` satisfies it. */
export type PopoverRect = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>;

export interface PlacePopoverInput {
  /** Element rect, or a zero-size cursor point. */
  anchor: PopoverRect;
  popover: { width: number; height: number };
  /** Widget card rect ∩ viewport. */
  boundary: PopoverRect;
  /** Distance between anchor and popover. Default 4. */
  gap?: number;
}

/** `min` wins over `max`, so a popover larger than the boundary pins to its start. */
const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

/**
 * Viewport position for a `position: fixed` popover. Below-start by default;
 * flips above when it would cross `boundary.bottom`; shifts horizontally to
 * stay inside the boundary.
 */
export function placePopover({ anchor, popover, boundary, gap = 4 }: PlacePopoverInput): {
  top: number;
  left: number;
} {
  const below = anchor.bottom + gap;
  const top = below + popover.height > boundary.bottom ? anchor.top - gap - popover.height : below;
  return {
    top: clamp(top, boundary.top, boundary.bottom - popover.height),
    left: clamp(anchor.left, boundary.left, boundary.right - popover.width),
  };
}
